import { useEffect, useRef } from 'react';
import { motion, useSpring, useTransform, type MotionValue } from 'framer-motion';
import { createSignalParticles } from './signal-particles';

const CHANNELS = [
  'M-80 70H100Q140 70 175 110L365 295Q400 330 450 330H800',
  'M-80 195H205Q245 195 275 225L360 310Q400 350 450 350H800',
  'M-80 560H115Q155 560 190 525L350 380Q385 350 450 350H800',
  'M1680 100H1500Q1460 100 1425 135L1255 300Q1225 330 1175 330H800',
  'M1680 225H1410Q1375 225 1340 255L1265 320Q1230 350 1175 350H800',
  'M1680 560H1490Q1450 560 1415 525L1250 380Q1215 350 1175 350H800',
];

/** The six streams and their travelling packets; `id` keeps gradient/filter ids unique per instance. */
export function SignalCircuit({ id }: { id: string }) {
  return <>
  <svg className="moova-signal-circuit" viewBox="0 0 1600 620" fill="none" focusable="false">
    <defs>
      <linearGradient id={`moova-signal-spectrum-${id}`} x1="0" y1="0" x2="1600" y2="0" gradientUnits="userSpaceOnUse">
        <stop stopColor="#679fff" stopOpacity="0" /><stop offset=".2" stopColor="#86b2ff" /><stop offset=".5" stopColor="#bcd5ff" /><stop offset=".8" stopColor="#79bbee" /><stop offset="1" stopColor="#79bbee" stopOpacity="0" />
      </linearGradient>
      <filter id={`moova-signal-glow-${id}`} x="-10%" y="-100%" width="120%" height="300%"><feGaussianBlur stdDeviation="5" /></filter>
    </defs>
    {CHANNELS.map((d, i) => <g key={d}>
      <path d={d} stroke={`url(#moova-signal-spectrum-${id})`} strokeWidth="1" opacity=".2" />
      <path d={d} stroke={`url(#moova-signal-spectrum-${id})`} strokeWidth="8" opacity=".14" filter={`url(#moova-signal-glow-${id})`} />
      <path className="moova-signal-packet" d={d} pathLength="100" stroke={`url(#moova-signal-spectrum-${id})`} strokeWidth="1.8" strokeLinecap="round" strokeDasharray="3 97" style={{ animationDelay: `${-i * 1.3}s` }} />
    </g>)}
    <g stroke="#9db8e8" strokeWidth="1" opacity=".4">
      <rect x="169" y="104" width="9" height="9" rx="2" /><rect x="270" y="220" width="7" height="7" rx="1" /><rect x="185" y="520" width="9" height="9" rx="2" />
      <rect x="1420" y="130" width="9" height="9" rx="2" /><rect x="1335" y="250" width="7" height="7" rx="1" /><rect x="1410" y="520" width="9" height="9" rx="2" />
    </g>
  </svg>
  </>;
}

/** Streams converge on the actual product, keeping the animation about playback. */
export function SignalField({ progress, reduced, focusY = 600, productWidth = 700 }: { progress: MotionValue<number>; reduced: boolean; focusY?: number; productWidth?: number }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const camera = useSpring(progress, { stiffness: 55, damping: 28, restDelta: .0001 });
  const top = useTransform(camera, [0, .35], [focusY, focusY - productWidth * .12]);
  const scale = useTransform(camera, [0, .4, 1], [1, 1.08, .96]);
  const opacity = useTransform(camera, [0, .3, .65, 1], [.85, .35, .35, .7]);
  const gridY = useTransform(camera, [0, 1], [0, -40]);

  useEffect(() => {
    if (!canvas.current) return;
    return createSignalParticles(canvas.current, () => progress.get(), reduced);
  }, [progress, reduced]);

  return <div className="moova-signal-field" aria-hidden="true">
    <div className="moova-signal-light" />
    <motion.div className="moova-signal-streams" style={reduced ? { top: focusY } : { top, scale, opacity }}>
      <SignalCircuit id="hero" />
    </motion.div>
    <canvas ref={canvas} className="moova-signal-particles" />
    <motion.div className="moova-signal-floor" style={reduced ? undefined : { y: gridY }}><div className="moova-signal-grid" /></motion.div>
    <div className="moova-signal-vignette" />
  </div>;
}
